import { cloneDeep } from 'es-toolkit';
import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Delete } from '@mui/icons-material';
import { Box, Button } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { AddLoadTo, ManualLoadSettings } from '@paragon/contract-library';
import { getManualLoadDisplayName, PropertiesPanel, PropertiesSubpanel } from '@paragon/layout-models';

import { RootState } from '../../../../store';
import { finishInsertingManualLoads } from '../../../../store/actions/insertActions';
import { calculateActiveComponentMembersMinMax } from '../../../../store/selectors/boundsSelectors';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getLoadCasesForActiveComponentDesignSorted } from '../../../../store/selectors/getLoadCasesForActiveComponentDesignSortedSelector';
import { ManualLoadFields } from '../manual-loads/ManualLoadFields';
import { AddManualLoadToSelect } from './AddManualLoadToSelect';
import InsertionPanelContainer from './InsertionPanelContainer';
import {
  distributedManualLoadDefinitionHasDifferentStartAndEnd,
  distributedManualLoadHasDifferentStartAndEnd,
  manualDistributedLoadDefinitionMagnitudeIsGreaterThanZero,
  manualDistributedLoadMagnitudeIsGreaterThanZero,
  manualLoadCoordinateIsOnComponent,
  manualLoadMagnitudeIsGreaterThanZero,
} from './manualLoadValidation';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;
interface State {
  manualLoadSettingsBeingEdited: ManualLoadSettings;
  manualLoadsToAdd: ManualLoadSettings[];
  changesOnFields: boolean;
}

class LoadInsertionSubPanel extends React.Component<Props, State> {
  state: State = {
    manualLoadSettingsBeingEdited: {
      guid: crypto.randomUUID(),
      load: {
        loadInsertionMethod: 'ProjectedCoordinates',
        loadDefinition: {
          guid: crypto.randomUUID(),
          distribution: 'Concentrated',
          definitionAxis: 'X',
          loadType: 'Dead',
          memberType: 'TopChord',
          magnitude: 0,
          loadDirection: 'GlobalDown',
          coordinate: this.props.componentBounds?.minX ?? 0,
          name: null,
        },
      },
      loadCaseIdentification: AddLoadTo.LoadCasesWithSpecifiedLoadType,
      specifiedLoadCaseName: null,
    },
    manualLoadsToAdd: [],
    changesOnFields: true,
  };

  manualLoadSettingsBeingEditedAreValid = () => {
    if (!this.props.componentBounds) throw new Error('Component bounds not found when validating manual load settings');

    const { load } = this.state.manualLoadSettingsBeingEdited;
    if (load.loadInsertionMethod === 'ProjectedCoordinates') {
      const { loadDefinition } = load;
      const validateCoordinateIsOnComponent = manualLoadCoordinateIsOnComponent(this.props.componentBounds);
      if (loadDefinition.distribution === 'Concentrated') {
        return (
          validateCoordinateIsOnComponent(loadDefinition.definitionAxis, loadDefinition.coordinate) &&
          manualLoadMagnitudeIsGreaterThanZero(loadDefinition.magnitude, 'Pounds')
        );
      }
      return (
        validateCoordinateIsOnComponent(loadDefinition.definitionAxis, loadDefinition.startCoordinate) &&
        validateCoordinateIsOnComponent(loadDefinition.definitionAxis, loadDefinition.endCoordinate) &&
        manualDistributedLoadDefinitionMagnitudeIsGreaterThanZero(loadDefinition) &&
        distributedManualLoadDefinitionHasDifferentStartAndEnd(load.loadDefinition)
      );
    }

    const validateCoordinateIsOnComponent = manualLoadCoordinateIsOnComponent(this.props.componentBounds);
    const { load: manualLoad } = load;
    if (manualLoad.distribution === 'Concentrated') {
      return (
        validateCoordinateIsOnComponent('X', manualLoad.start.x) &&
        validateCoordinateIsOnComponent('Y', manualLoad.start.y) &&
        manualLoadMagnitudeIsGreaterThanZero(manualLoad.startMagnitude, manualLoad.magnitudeUnit)
      );
    }
    return (
      validateCoordinateIsOnComponent('X', manualLoad.start.x) &&
      validateCoordinateIsOnComponent('Y', manualLoad.start.y) &&
      validateCoordinateIsOnComponent('X', manualLoad.end.x) &&
      validateCoordinateIsOnComponent('Y', manualLoad.end.y) &&
      manualDistributedLoadMagnitudeIsGreaterThanZero(manualLoad) &&
      distributedManualLoadHasDifferentStartAndEnd(manualLoad)
    );
  };

  onAddLoad = () => {
    this.setState({ changesOnFields: false });

    if (!this.props.componentDesign) return;

    const { manualLoadSettingsBeingEdited: manualLoadSettings } = this.state;
    const { loadCaseIdentification, specifiedLoadCaseName } = manualLoadSettings;

    if (!specifiedLoadCaseName && loadCaseIdentification === AddLoadTo.SpecificLoadCase) return;

    if (this.props.insertionInfo && this.props.insertionInfo.type === 'load' && this.props.componentDesign) {
      this.setState((oldState) => ({
        manualLoadsToAdd: [...oldState.manualLoadsToAdd, cloneDeep(manualLoadSettings)],
        manualLoadSettingsBeingEdited: { ...oldState.manualLoadSettingsBeingEdited, guid: crypto.randomUUID() },
      }));
    }
  };

  onDeleteManualLoad = (index: number) => () => {
    this.setState((oldState) => {
      const newManualLoadArray = [...oldState.manualLoadsToAdd];
      newManualLoadArray.splice(index, 1);
      return {
        manualLoadsToAdd: newManualLoadArray,
      };
    });
  };

  insertManualLoads = () => {
    if (!this.props.componentDesign) return;
    this.props.finishInsertingManualLoads(this.state.manualLoadsToAdd);
  };

  render() {
    const { componentDesign, componentBounds, insertionInfo, loadCases } = this.props;

    const {
      manualLoadSettingsBeingEdited: { load: loadOrLoadDefinition, loadCaseIdentification, specifiedLoadCaseName },
    } = this.state;

    if (!insertionInfo || !componentDesign || !componentBounds) return null;

    let showManualLoadArrayValues;
    if (this.state.manualLoadsToAdd.length > 0) {
      showManualLoadArrayValues = (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {this.state.manualLoadsToAdd.map((manualLoadSettings, index) => (
            <Box key={manualLoadSettings.guid}>
              {getManualLoadDisplayName(manualLoadSettings)}
              <TooltipIconButton title="Delete Manual Load" onClick={this.onDeleteManualLoad(index)}>
                <Delete />
              </TooltipIconButton>
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <InsertionPanelContainer disabled={this.state.manualLoadsToAdd.length === 0} onInsert={this.insertManualLoads}>
        <PropertiesPanel title="Manual Load Insertion">
          <PropertiesSubpanel title="Define Load">
            <ManualLoadFields
              loadOrLoadDefinition={this.state.manualLoadSettingsBeingEdited.load}
              component={componentDesign.component}
              onChangeLoadOrLoadDefinition={(updatedLoadOrLoadDefinition) =>
                void this.setState((oldState) => ({
                  manualLoadSettingsBeingEdited: {
                    ...oldState.manualLoadSettingsBeingEdited,
                    load: updatedLoadOrLoadDefinition,
                  },
                  changesOnFields: true,
                }))
              }
              coordinateIsOnComponent={manualLoadCoordinateIsOnComponent(componentBounds)}
              submitIfInvalid
            />
            <Box sx={{ display: 'flex' }}>
              <AddManualLoadToSelect
                sx={{ m: 1, flex: 1 }}
                loadCases={loadCases}
                componentDesign={componentDesign}
                loadType={
                  loadOrLoadDefinition.loadInsertionMethod === 'Points'
                    ? loadOrLoadDefinition.load.loadType
                    : loadOrLoadDefinition.loadDefinition.loadType
                }
                loadCaseIdentification={loadCaseIdentification}
                specifiedLoadCaseName={specifiedLoadCaseName}
                onSelectLoadDestination={(loadCaseIdentification, specifiedLoadCaseName) =>
                  this.setState((oldState) => ({
                    manualLoadSettingsBeingEdited: {
                      ...oldState.manualLoadSettingsBeingEdited,
                      loadCaseIdentification,
                      specifiedLoadCaseName,
                    },
                  }))
                }
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                sx={{ m: 2, width: '200px' }}
                variant="outlined"
                size="small"
                onClick={this.onAddLoad}
                disabled={
                  !this.state.changesOnFields ||
                  specifiedLoadCaseName === 'Select' ||
                  !this.manualLoadSettingsBeingEditedAreValid()
                }
              >
                Add Load
              </Button>
            </Box>
            {showManualLoadArrayValues}
          </PropertiesSubpanel>
        </PropertiesPanel>
      </InsertionPanelContainer>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  let { insertingObjectInfo } = state.application;
  if (insertingObjectInfo && insertingObjectInfo.type !== 'load') {
    insertingObjectInfo = null;
  }

  return {
    insertionInfo: insertingObjectInfo,
    componentDesign: getActiveComponentDesign(state),
    componentBounds: calculateActiveComponentMembersMinMax(state),
    loadCases: getLoadCasesForActiveComponentDesignSorted(state),
  };
};

const actionCreators = {
  finishInsertingManualLoads,
};

export default connect(mapStateToProps, actionCreators)(LoadInsertionSubPanel);
